import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Box, Button, Link, Checkbox, TextField, Typography, InputAdornment } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import * as ProfilesService from 'services/api/ProfilesService';
import { UserContext } from 'components/UserGuard';
import Modal from 'components/shared/Modal';
import { useWindowSize } from 'util/Window';
import styles from 'components/profile/EditProfilePageView/EditProfileForm/EditProfileForm.module.scss';
import { VerifyPhoneButton } from '../VerifyPhoneButton';
import { InfoOutlined } from '@mui/icons-material';
import {tenantConfig} from 'config'
import { DIGITAL_WALLET_CARD_POPUP_HIDDEN_LABEL, getExpDate } from 'util/LocalStorage';
import { DWCModalInfo } from '../AccessDigitalWalletCardModal/AccessDigitalWalletCardModal';
import { validatePhoneE164 } from 'utils/phoneFormatter';
import { trackEvent } from 'util/gtm';

export const ExternalAccountModal: React.FunctionComponent = () => {
  const { isMobile } = useWindowSize();
  const user = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [builderFaxAgreement, setBuilderFaxAgreement] = useState(false);
  const [phone, setPhone] = useState(user.phone)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const enabled = tenantConfig.builderFaxEnabled
  const [verified, setVerified] = useState< any | undefined>(undefined);
  
  useEffect(() => {
    async function load() {
      if(user.nccerCardNumber){
      const res = await ProfilesService.GetUserVerified(user.nccerCardNumber);
      setVerified(res);}
      else{
        setVerified("didn't work")
      }
    }
    load().catch((error) => console.error(error));
  }, [user]);

  const [openDWC, setOpenDWC] = useState(false);
  const phoneCodeSubmittable = useMemo(() => phone && phone?.replace("+", "").length > 10 ,[phone])

  const verifiedPhone = useMemo(() => verified && Array.isArray(verified)? verified.find((items: { name: string; value: string }) => items.name === "phone_number") : null,[verified, user])
  const verifiedPhoneVerified = useMemo(() => verified && Array.isArray(verified)? verified.find((items: { name: string; value: string }) => items.name === "phone_number_verified") : null,[verified, user])
  const [display, setDisplay] = React.useState<boolean | null>(null)

   useEffect(() => {
    if(!phone || (phone && !/\d/.test(phone))){
      //if there is no phone no phone number
      setDisplay(null)
      setPhoneVerified(false)
    } else if (verifiedPhoneVerified?.value == "true" && phone?.replace("+", "") == verifiedPhone?.value?.replace("+", "")){
      //We do not store the + because aws. If the current number is verified, mark verified
      setDisplay(true)
      setPhoneVerified(true)
    } else {
      //if the current phone is there and not verified, or changed
      setDisplay(false)
      setPhoneVerified(false)
    }
  }, [verified, phone, verifiedPhoneVerified, verifiedPhone, user])

  
  const DisplayVerified = useMemo(() => {
    if(display == null){
      return <Typography color='red' display={'flex'} alignItems={'center'} justifyContent={'center'}>No Phone Number</Typography>
    }
    else if (display){
      return <Typography color='green' display={'flex'} alignItems={'center'} justifyContent={'center'}> <CheckCircleOutline />Verified</Typography>
    }
    return <Typography color='orange' display={'flex'} alignItems={'center'} justifyContent={'center'}>Unverified</Typography>
  }, [display, user])

  const PhoneVerified = useCallback(() => {
    user.refetchUser();
  }, []);

  const openModal = useCallback(async () => {
    setOpen(true);
  }, []);

  const handleManualOpen = useCallback(() => {
    trackEvent('builderfax_modal_display', { phone_verified: phoneVerified, auto_display: false });
    openModal();
  }, [openModal, phoneVerified]);

  const closeModal = useCallback(() => {
    setBuilderFaxAgreement(false)
    suppressModalPopup(user.nccerCardNumber)
    setOpen(false)
  }, []);

  const handleNotNow = useCallback(() => {
    trackEvent('builderfax_not_now');
    closeModal();
  }, [closeModal]);

  const handleXClose = useCallback(() => {
    trackEvent('builderfax_modal_close');
    closeModal();
  }, [closeModal]);

  const closeDWCModal = useCallback(() => {
    trackEvent('builderfax_got_it');
    user.refetchUser();
    setOpenDWC(false)
  }, []);
  
  const onSubmit = useCallback(async () => {
    setLoading(true)
    try{
      const nccerCardNumber = user.nccerCardNumber ?? '';
      const digitalWalletCardStatus = user.digitalWalletCardOptIn ?? false;
      trackEvent('builderfax_activate', { digital_wallet_card_opt_in: digitalWalletCardStatus });
      await ProfilesService.externalAccountOptIn(nccerCardNumber, digitalWalletCardStatus)
      setLoading(false)
      setOpen(false)
      setOpenDWC(true)
    } catch (error){
      //handle error here
      setLoading(false)
    }}, [user]
  );

  useEffect(() => {
    if(!user.digitalWalletCardOptIn && JSON.parse(enabled) && !isSuppressedPopup(user.nccerCardNumber)){
      trackEvent('builderfax_modal_display', { phone_verified: phoneVerified, auto_display: true });
      openModal()
    }
  }, [openModal])

  return (
    <ExternalAccountModalContext.Provider value={{ setLoading, closeModal }}>
      {!user.digitalWalletCardOptIn && JSON.parse(enabled) && <Button
        variant="text"
        color="primary"
        disabled={loading}
        onClick={handleManualOpen}
        sx={{ display: 'flex', gap: 1, width: '100%', height: 'fit-content', justifyContent: 'left',  background: "#F8F9FC", borderBottom: "1px solid #D5D7DA", paddingLeft: "15px"}}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: "top", padding: "2px" }}>
          <InfoOutlined /><Typography variant='body2' sx={{fontWeight: "400", fontSize: "16px", alignItems: "top", marginTop:'3px'}}><b style={{fontWeight:"500", color:"black"}}>Digital Wallet. </b> <Link color="secondary">Activate your NCCER digital badges</Link> to view, share, and manage your credentials on your phone.</Typography>
        </Box>
      </Button>}
      <Modal
        loading={loading}
        open={open}
        title={"Access Your Mobile Credentials"}
        subTitleText={''}
        onClose={handleXClose}
        actions={
          <Box sx={{display:"flex", flexDirection: 'row', justifyContent: 'right', gap:2, width:1, pt:4}}>
            <Button color="secondary" variant="outlined" onClick={handleNotNow}>
              Not now
            </Button>
            <Button color="primary" variant="contained" onClick={onSubmit} disabled={!(builderFaxAgreement && phoneVerified)}>
              Activate My BuilderFax Account
            </Button>
          </Box>
        }
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex',  }}>
            <img src='/images/nccer/images/NccerBuilderfax.png'  width="100%"/>
          </Box>
          <Box sx={{ display: 'flex', marginTop: '8px' }}>
            Access your digital badges on your phone to view and share your NCCER credentials and upload other certifications, like OSHA.
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '10px' }}>
          This one-time mobile number verification signs you up for BuilderFax. Enter your correct mobile number if it hasn't been verified.
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column',  marginTop: '10px' }}>
          <Box sx={{ display: 'flex', flexDirection: isMobile? 'column' : 'row', marginTop: '10px', gap: 3 }}>
          <TextField
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            required={true}
            disabled={false}
            className={styles.inputFieldLeft}
            sx={{ width: '100%', paddingRight: isMobile? "0px!important" : 'auto', justifyContent: "end",}}
            InputProps={{ endAdornment: <InputAdornment position="end">{DisplayVerified}</InputAdornment>}}
            // Slot props will be used in a future mui update. Input props will look like this instead
            // slotProps={{
            //   input: {
            //     endAdornment: <InputAdornment position="end">{DisplayVerified}</InputAdornment>,
            //   },
            // }}
          />
          {!phoneVerified && <VerifyPhoneButton disabled={!validatePhoneE164(phone) || !phoneCodeSubmittable } phone={phone? phone : ''} exitEditing={PhoneVerified}/>}
          </Box>
          {validatePhoneE164(phone) != true && <Typography color='error' variant='subtitle2'>{validatePhoneE164(phone)}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column',   marginTop: '20px' }}>
          <Box sx={{ display: 'flex',   marginTop: '5px', flexDirection: 'row', alignItems: "center", gap: 1 }}>
            <Checkbox value={builderFaxAgreement} onChange={(e) => setBuilderFaxAgreement(e.target.checked)}></Checkbox>{'I agree to the '}<Link href="https://www.builderfax.com/terms-of-service" target="_blank">BuilderFax Terms and Conditions</Link>{" and "}<Link href="https://www.builderfax.com/privacy-policy" target="_blank">Privacy Policy</Link>
          </Box>
        </Box>
      </Modal>

      <Modal 
        loading={loading} 
        open={openDWC} 
        title={"Access Your Mobile Credentials"} 
        subTitleText={''} 
        onClose={closeDWCModal} 
        actions={
          <Box sx={{display:"flex", flexDirection: 'row', justifyContent: 'right', gap:2, width:1, pt:4}}>
            <Button color="secondary" variant="outlined" onClick={closeDWCModal}>
              Got It
            </Button>
          </Box>
        }
        >
        <DWCModalInfo/>
      </Modal>
    </ExternalAccountModalContext.Provider>
  );
}

interface ExternalAccountModalContext {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

const ExternalAccountModalContext = createContext<ExternalAccountModalContext>({
  setLoading: () => undefined,
  closeModal: () => undefined,
});

function isSuppressedPopup(nccerCardNumber: string | undefined): boolean {
  const key = DIGITAL_WALLET_CARD_POPUP_HIDDEN_LABEL + nccerCardNumber;
  const value = localStorage.getItem(key);
  const expiry = value ? parseFloat(value) : 0;

  return Boolean(expiry) && expiry > Date.now();
}

// Suppress automatic wallet card popup on page load.
function suppressModalPopup(nccerCardNumber: string | undefined): void {
  const key = DIGITAL_WALLET_CARD_POPUP_HIDDEN_LABEL + nccerCardNumber;
  // Suppress popup for a year.
  const timestamp = getExpDate(365) 
  localStorage.setItem(key, String(timestamp));
}